// NarrowingList — the shared SHELL for the "capability + attached narrowing list"
// shape (ems-domain/PERMISSIONS.md §Capability + narrowing list): a labelled list,
// tied by a brand left-border to the capability it narrows. THREE instances reuse
// this shell, each supplying its OWN rows + add control — deliberately NOT a
// config-driven framework ("reuse the shape, not a framework"; instances ≠ a
// framework):
//   • users:manage      → managed-roles      — entity chips (TagGroup)
//   • ticket.work_order → (type, level) list — rows: entity + a SegmentedControl level
//   • responsibility    → level-set          — level chips over a fixed enum
//
// The shell owns the framing (label · description · the brand tie · entries layout ·
// the add slot). The instance owns what a row IS and what "add" picks (a Picker /
// Menu). Slots over flags ([COMPONENTS §Component taxonomy]).

import type { ReactNode } from 'react';

export interface NarrowingListProps {
  /** Sub-label naming the narrowing (e.g. "Delegated roles", "Work orders"). */
  label: ReactNode;
  /** One-line explainer (e.g. "Holders may assign only these roles"). */
  description?: ReactNode;
  /** The selected entries — instance-specific (TagGroup chips, or rows with a level). */
  children: ReactNode;
  /** The add affordance — instance-specific (a Picker / Menu). Rendered last. */
  add?: ReactNode;
  className?: string;
}

export function NarrowingList({ label, description, children, add, className = '' }: NarrowingListProps) {
  return (
    <div className={`ms-1 border-s-2 border-[var(--color-primary-display)]/40 ps-3 ${className}`}>
      <p className="text-sm font-medium text-[var(--color-text-primary)]">{label}</p>
      {description && <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{description}</p>}
      <div className="mt-2 flex flex-col gap-2">{children}</div>
      {add && <div className="mt-2">{add}</div>}
    </div>
  );
}
